import { slugify } from './routes.js';
import type {
  AlbumDetail,
  AlbumFacetCounts,
  AlbumFilters,
  AlbumGroup,
  AlbumGroupField,
  AlbumSearchResult,
  AlbumSummary,
  FacetOption,
  PickerMode,
  PickHistoryEntry,
  QualityFilter,
  SearchDocument,
  SearchReason,
  SortOption,
  Track,
} from '../types';

export function formatSampleRate(rate: number | null): string {
  if (!rate) return 'Unknown rate';
  if (rate >= 2_822_400) return formatDsdRate(rate);
  return `${Number.parseFloat((rate / 1_000).toFixed(3))} kHz`;
}

// ffprobe reports DSF streams at an eighth of the DSD bit rate (352.8 kHz for DSD64).
export function formatDsdRate(rate: number | null): string {
  if (!rate) return 'DSD';
  const hz = rate < 1_411_200 ? rate * 8 : rate;
  return `DSD${Math.max(64, Math.round(hz / 44_100))}`;
}

function isDsdOnly(album: Pick<AlbumSummary, 'formats'>): boolean {
  return album.formats.length === 1 && album.formats[0] === 'DSD';
}

// The resolution without a format prefix, or null when the files do not report one.
export function albumResolutionDetail(album: AlbumSummary): string | null {
  if (isDsdOnly(album)) return [...new Set(album.sampleRates.map(formatDsdRate))].join('–') || 'DSD';
  if (!album.sampleRates.length) return null;
  const rates = album.sampleRates.map((rate) => formatSampleRate(rate).replace(' kHz', ''));
  const rate = rates.length === 1 ? `${rates[0]} kHz` : `${rates[0]}–${rates.at(-1)} kHz`;
  if (!album.bitDepths.length) return rate;
  const bits = album.bitDepths.length === 1 ? `${album.bitDepths[0]}-bit` : `${album.bitDepths[0]}–${album.bitDepths.at(-1)}-bit`;
  return `${bits}/${rate}`;
}

export function describeAlbumResolution(album: AlbumSummary): string {
  const detail = albumResolutionDetail(album);
  const formats = album.formats.join(' / ') || 'Unknown format';
  if (!detail) return formats;
  return album.lossless || isDsdOnly(album) ? detail : `${formats} · ${detail}`;
}

export function trackResolution(track: Track): string {
  if (track.codec === 'dsd') return formatDsdRate(track.sampleRateHz);
  if (!track.sampleRateHz) return track.codecLabel;
  if (!track.bitsPerSample) return `${track.codecLabel} · ${formatSampleRate(track.sampleRateHz)}`;
  return `${track.bitsPerSample}-bit/${formatSampleRate(track.sampleRateHz)}`;
}

export function summarizeDetail(album: AlbumDetail): AlbumSummary {
  const formats = [...new Set(album.tracks.map((track) => track.codecLabel))];
  const bitDepths = [...new Set(album.tracks.map((track) => track.bitsPerSample).filter((value): value is number => value !== null))].sort((a, b) => a - b);
  const sampleRates = [...new Set(album.tracks.map((track) => track.sampleRateHz).filter((value): value is number => value !== null))].sort((a, b) => a - b);
  const resolutionCount = new Set(album.tracks.map(trackResolution)).size;
  const ranges = album.tracks.map((track) => track.loudnessRangeLu).filter((value): value is number => value !== null);
  return {
    ...album,
    trackCount: album.tracks.length,
    discCount: Math.max(1, ...album.tracks.map((track) => track.discNumber)),
    durationSeconds: album.tracks.reduce((sum, track) => sum + track.durationSeconds, 0),
    fileSizeBytes: album.tracks.reduce((sum, track) => sum + track.fileSizeBytes, 0),
    formats,
    lossless: album.tracks.every((track) => track.lossless),
    flac: album.tracks.every((track) => track.codec === 'flac'),
    hiRes: album.tracks.some((track) => track.codec === 'dsd' || (track.lossless && ((track.bitsPerSample ?? 0) > 16 || (track.sampleRateHz ?? 0) > 48_000))),
    mixedResolution: resolutionCount > 1,
    bitDepths,
    sampleRates,
    channels: [...new Set(album.tracks.map((track) => track.channels).filter((value): value is number => value !== null))],
    loudnessRangeLu: ranges.length ? ranges.reduce((sum, value) => sum + value, 0) / ranges.length : null,
  };
}

export function filterAlbums(albums: AlbumSummary[], query: string, format: string, quality: QualityFilter): AlbumSummary[] {
  const needle = query.trim().toLowerCase();
  return albums.filter((album) => {
    const searchable = [album.artist, album.title, album.version, album.label, album.year, ...album.genres, ...album.formats].join(' ').toLowerCase();
    if (needle && !searchable.includes(needle)) return false;
    if (format !== 'all' && !album.formats.includes(format)) return false;
    if (quality === 'flac' && !album.flac) return false;
    if (quality === 'hires' && !album.hiRes) return false;
    if (quality === 'cd' && (album.hiRes || !album.lossless || album.formats.includes('DSD'))) return false;
    if (quality === 'dsd' && !album.formats.includes('DSD')) return false;
    if (quality === 'lossy' && album.lossless) return false;
    if (quality === 'mixed' && !album.mixedResolution) return false;
    return true;
  });
}

export function normalizeSearchText(value: string): string {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[’'"`]/g, '').replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
}

function textMatchScore(value: string, query: string, weight: number): number {
  const normalized = normalizeSearchText(value);
  if (!normalized || !query) return 0;
  if (normalized === query) return weight * 4;
  if (normalized.startsWith(query)) return weight * 3;
  if (normalized.split(' ').some((word) => word.startsWith(query))) return weight * 2;
  if (normalized.includes(query)) return weight;
  return 0;
}

export function albumQualityLabels(album: AlbumSummary): string[] {
  const labels: string[] = [];
  if (album.formats.includes('DSD')) labels.push('DSD');
  if (album.hiRes) labels.push('Hi-res');
  if (album.lossless && !album.hiRes && !album.formats.includes('DSD')) labels.push('CD resolution');
  if (!album.lossless) labels.push('Lossy');
  if (album.mixedResolution) labels.push('Mixed resolution');
  if (album.flac) labels.push('FLAC');
  return [...new Set(labels)];
}

export function defaultAlbumFilters(): AlbumFilters {
  return { query: '', artist: '', label: '', genres: [], formats: [], qualities: [], decades: [] };
}

const QUALITY_ALIASES: Record<string, (album: AlbumSummary) => boolean> = {
  'hi-res': (album) => album.hiRes,
  hires: (album) => album.hiRes,
  'cd-resolution': (album) => album.lossless && !album.hiRes && !album.formats.includes('DSD'),
  cd: (album) => album.lossless && !album.hiRes && !album.formats.includes('DSD'),
  lossless: (album) => album.lossless,
  lossy: (album) => !album.lossless,
  flac: (album) => album.flac,
  dsd: (album) => album.formats.includes('DSD'),
  'mixed-resolution': (album) => album.mixedResolution,
  mixed: (album) => album.mixedResolution,
};

export function isKnownQuality(value: string): boolean {
  return slugify(value) in QUALITY_ALIASES;
}

// Accepts the visible labels ("Hi-res") and the API vocabulary ("hires", "cd", "lossless").
export function matchesQuality(album: AlbumSummary, value: string): boolean {
  return QUALITY_ALIASES[slugify(value)]?.(album) ?? false;
}

function includesNormalized(values: string[], selected: string[]): boolean {
  if (!selected.length) return true;
  const normalized = values.map(slugify);
  return selected.some((value) => normalized.includes(slugify(value)));
}

interface ParsedQuery { text: string; filters: Partial<AlbumFilters>; year?: number; deep?: { field: 'track' | 'trackartist'; value: string } }

function parseStructuredQuery(query: string): ParsedQuery {
  const filters: Partial<AlbumFilters> = {};
  let deep: ParsedQuery['deep'];
  let exactYear: number | undefined;
  const remaining = query.replace(/\b(artist|label|genre|format|quality|decade|year|track|trackartist):(?:"([^"]+)"|([^\s]+))/gi, (_, field: string, quoted: string, bare: string) => {
    const value = quoted || bare || '';
    const key = field.toLowerCase();
    if (key === 'track' || key === 'trackartist') deep = { field: key, value };
    else if (key === 'artist' || key === 'label') filters[key] = value;
    else if (key === 'genre') filters.genres = [value];
    else if (key === 'format') filters.formats = [value];
    else if (key === 'quality') filters.qualities = [value];
    else {
      const year = Number.parseInt(value, 10);
      if (Number.isFinite(year)) { if (key === 'year') exactYear = year; else filters.decades = [Math.floor(year / 10) * 10]; }
    }
    return ' ';
  });
  return { text: remaining.trim(), filters, deep, year: exactYear };
}

// Collection pages search the same inputs several times per keystroke; reuse the last result.
let lastSearch: { albums: AlbumSummary[]; query: string; index: SearchDocument[]; results: AlbumSearchResult[] } | null = null;

export function searchAlbums(albums: AlbumSummary[], rawQuery: string, searchIndex: SearchDocument[] = []): AlbumSearchResult[] {
  if (lastSearch && lastSearch.albums === albums && lastSearch.query === rawQuery && lastSearch.index === searchIndex) return lastSearch.results;
  const results = runSearch(albums, rawQuery, searchIndex);
  lastSearch = { albums, query: rawQuery, index: searchIndex, results };
  return results;
}

function runSearch(albums: AlbumSummary[], rawQuery: string, searchIndex: SearchDocument[]): AlbumSearchResult[] {
  const { text, filters, deep, year } = parseStructuredQuery(rawQuery);
  const eligible = filterLibraryAlbums(albums, { ...defaultAlbumFilters(), ...filters }).filter((album) => year === undefined || album.year === year);
  const query = normalizeSearchText(deep?.value || text);
  const documents = new Map(searchIndex.map((document) => [document.id, document]));
  if (!query) return eligible.map((album) => ({ album, score: 1, reasons: [] }));
  return eligible.map((album): AlbumSearchResult => {
    const document = documents.get(album.id);
    const fields: Array<[SearchReason, string[], number]> = [
      ['Title', [album.title], 120],
      ['Artist', [album.artist], 110],
      ['Label', [album.label || ''], 80],
      ['Genre', album.genres, 70],
      ['Format', album.formats, 55],
      ['Year', [String(album.year || '')], 65],
      ['Quality', albumQualityLabels(album), 55],
      ['Resolution', [describeAlbumResolution(album)], 55],
      ['Version', [album.version || ''], 45],
      ['Track title', document?.trackTitles || [], 100],
      ['Track artist', document?.trackArtists || [], 90],
    ];
    let score = 0;
    const reasons: SearchReason[] = [];
    for (const [reason, values, weight] of fields) {
      if (deep && ((deep.field === 'track' && reason !== 'Track title') || (deep.field === 'trackartist' && reason !== 'Track artist'))) continue;
      const fieldScore = Math.max(0, ...values.map((value) => textMatchScore(value, query, weight)));
      if (fieldScore > 0) { score += fieldScore; reasons.push(reason); }
    }
    return { album, score, reasons };
  }).filter((result) => result.score > 0).sort((a, b) => b.score - a.score || a.album.artist.localeCompare(b.album.artist));
}

type FacetKey = 'artist' | 'label' | 'genres' | 'formats' | 'qualities' | 'decades';

function applyFacetFilters(albums: AlbumSummary[], filters: AlbumFilters, skip?: FacetKey): AlbumSummary[] {
  return albums.filter((album) => (skip === 'artist' || !filters.artist || slugify(album.artist) === slugify(filters.artist))
    && (skip === 'label' || !filters.label || slugify(album.label || '') === slugify(filters.label))
    && (skip === 'genres' || includesNormalized(album.genres, filters.genres))
    && (skip === 'formats' || includesNormalized(album.formats, filters.formats))
    && (skip === 'qualities' || !filters.qualities.length || filters.qualities.some((quality) => matchesQuality(album, quality)))
    && (skip === 'decades' || !filters.decades.length || (album.year !== null && filters.decades.includes(Math.floor(album.year / 10) * 10))));
}

// Results keep search relevance order when there is a query.
export function filterLibraryAlbums(albums: AlbumSummary[], filters: AlbumFilters, searchIndex: SearchDocument[] = []): AlbumSummary[] {
  const base = filters.query.trim() ? searchAlbums(albums, filters.query, searchIndex).map((item) => item.album) : albums;
  return applyFacetFilters(base, filters);
}

function countValues(values: string[]): FacetOption[] {
  const counts = new Map<string, FacetOption>();
  for (const value of values.filter(Boolean)) { const key = slugify(value); const previous = counts.get(key); counts.set(key, { value: previous?.value || value, count: (previous?.count || 0) + 1 }); }
  return [...counts.values()].sort((a, b) => b.count - a.count || a.value.localeCompare(b.value));
}

export function getAlbumFacets(albums: AlbumSummary[]): AlbumFacetCounts {
  return {
    artists: countValues(albums.map((album) => album.artist)),
    labels: countValues(albums.map((album) => album.label || '')),
    genres: countValues(albums.flatMap((album) => album.genres)),
    formats: countValues(albums.flatMap((album) => album.formats)),
    qualities: countValues(albums.flatMap(albumQualityLabels)),
    decades: countValues(albums.filter((album) => album.year !== null).map((album) => `${Math.floor(album.year! / 10) * 10}s`)),
  };
}

// Each group counts what its options would return alongside every other active filter.
export function getContextualFacets(albums: AlbumSummary[], filters: AlbumFilters, searchIndex: SearchDocument[] = []): AlbumFacetCounts {
  const base = filters.query.trim() ? searchAlbums(albums, filters.query, searchIndex).map((item) => item.album) : albums;
  const without = (key: FacetKey) => applyFacetFilters(base, filters, key);
  return {
    artists: getAlbumFacets(without('artist')).artists,
    labels: getAlbumFacets(without('label')).labels,
    genres: getAlbumFacets(without('genres')).genres,
    formats: getAlbumFacets(without('formats')).formats,
    qualities: getAlbumFacets(without('qualities')).qualities,
    decades: getAlbumFacets(without('decades')).decades,
  };
}

export function groupAlbums(albums: AlbumSummary[], field: AlbumGroupField): AlbumGroup[] {
  // Spelling variants that share a URL (Tool/TOOL) form one group under the most common spelling.
  const groups = new Map<string, { albums: AlbumSummary[]; spellings: Map<string, number> }>();
  for (const album of albums) {
    let values: string[];
    if (field === 'artist') values = [album.artist];
    else if (field === 'label') values = [album.label || 'Unknown label'];
    else if (field === 'genre') values = album.genres.length ? album.genres : ['Unclassified'];
    else if (field === 'format') values = album.formats.length ? album.formats : ['Unknown format'];
    else if (field === 'quality') values = albumQualityLabels(album);
    else values = [album.year === null ? 'Unknown decade' : `${Math.floor(album.year / 10) * 10}s`];
    for (const value of values) {
      const key = slugify(value) || value;
      const group = groups.get(key) || { albums: [] as AlbumSummary[], spellings: new Map<string, number>() };
      group.albums.push(album);
      group.spellings.set(value, (group.spellings.get(value) || 0) + 1);
      groups.set(key, group);
    }
  }
  return [...groups.entries()].map(([key, group]) => ({ key, label: [...group.spellings].sort((a, b) => b[1] - a[1])[0][0], albums: group.albums }))
    .sort((a, b) => b.albums.length - a.albums.length || a.label.localeCompare(b.label));
}

export function relatedAlbums(album: AlbumSummary, albums: AlbumSummary[], limit = 6): Array<{ album: AlbumSummary; reason: string }> {
  return albums.filter((item) => item.id !== album.id).map((item) => {
    const sharedGenres = item.genres.filter((genre) => album.genres.includes(genre));
    const sameArtist = normalizeSearchText(item.artist) === normalizeSearchText(album.artist);
    const sameLabel = Boolean(album.label) && normalizeSearchText(item.label || '') === normalizeSearchText(album.label || '');
    const sharedQuality = albumQualityLabels(item).filter((quality) => albumQualityLabels(album).includes(quality));
    const score = (sameArtist ? 100 : 0) + sharedGenres.length * 18 + (sameLabel ? 12 : 0) + sharedQuality.length * 3;
    const reason = sameArtist ? `More by ${album.artist}` : sharedGenres[0] ? `Also ${sharedGenres[0]}` : sameLabel ? `Also on ${album.label}` : sharedQuality[0] ? `Also ${sharedQuality[0]}` : '';
    return { album: item, score, reason };
  }).filter((item) => item.score > 0).sort((a, b) => b.score - a.score).slice(0, limit).map(({ album: item, reason }) => ({ album: item, reason }));
}

export function pickAlbum(eligible: AlbumSummary[], history: PickHistoryEntry[], mode: PickerMode, random: () => number = Math.random, currentId?: string): { album: AlbumSummary | null; eligibleCount: number; explanation: string } {
  if (!eligible.length) return { album: null, eligibleCount: 0, explanation: 'No albums match these constraints. Remove a filter and try again.' };
  let pool = [...eligible];
  if (mode === 'new-arrivals') {
    pool = pool.filter((album) => album.dateAdded && Number.isFinite(Date.parse(album.dateAdded))).sort((a, b) => Date.parse(b.dateAdded!) - Date.parse(a.dateAdded!)).slice(0, 36);
    if (!pool.length) return { album: null, eligibleCount: 0, explanation: 'Dates added have not been recorded for these albums. Choose another mode.' };
  }
  const recent = new Set(history.slice(0, 12).map((entry) => entry.albumId));
  if (pool.length >= 24) pool = pool.filter((album) => album.id !== currentId && !recent.has(album.id));
  if (!pool.length) pool = [...eligible];
  let album: AlbumSummary;
  if (mode === 'rediscover' || mode === 'deep-library') {
    const counts = new Map<string, number>();
    for (const entry of history) counts.set(entry.albumId, (counts.get(entry.albumId) || 0) + 1);
    const weights = pool.map((item) => 1 / (1 + (counts.get(item.id) || 0)));
    let threshold = random() * weights.reduce((sum, weight) => sum + weight, 0);
    album = pool.at(-1)!;
    for (let index = 0; index < pool.length; index += 1) { threshold -= weights[index]; if (threshold <= 0) { album = pool[index]; break; } }
  } else album = pool[Math.min(pool.length - 1, Math.floor(random() * pool.length))];
  const explanation = mode === 'new-arrivals' ? `Chosen from the ${pool.length} newest eligible albums.` : mode === 'rediscover' || mode === 'deep-library' ? `Chosen from ${eligible.length.toLocaleString('en-US')} eligible albums, favoring albums picked less often.` : `Chosen evenly from ${eligible.length.toLocaleString('en-US')} eligible albums. Recent picks were avoided when the pool allowed it.`;
  return { album, eligibleCount: eligible.length, explanation };
}

export function sortAlbums(albums: AlbumSummary[], sort: SortOption): AlbumSummary[] {
  const result = [...albums];
  if (sort === 'relevance') return result;
  const text = (a: AlbumSummary, b: AlbumSummary, field: 'artist' | 'title') => a[field].localeCompare(b[field], undefined, { sensitivity: 'base' });
  const releaseTime = (album: AlbumSummary) => {
    if (album.releaseDate) {
      const timestamp = Date.parse(album.releaseDate);
      if (Number.isFinite(timestamp)) return timestamp;
    }
    return album.year === null ? null : Date.UTC(album.year, 0, 1);
  };
  const releaseOrder = (a: AlbumSummary, b: AlbumSummary, direction: 1 | -1) => {
    const aTime = releaseTime(a);
    const bTime = releaseTime(b);
    const fallback = () => text(a, b, 'artist') || text(a, b, 'title');
    if (aTime === null) return bTime === null ? fallback() : 1;
    if (bTime === null) return -1;
    return direction * (aTime - bTime) || fallback();
  };
  result.sort((a, b) => {
    if (sort === 'title') return text(a, b, 'title') || text(a, b, 'artist');
    if (sort === 'release-desc') return releaseOrder(a, b, -1);
    if (sort === 'release-asc') return releaseOrder(a, b, 1);
    if (sort === 'size') return b.fileSizeBytes - a.fileSizeBytes;
    if (sort === 'resolution') return Math.max(...b.sampleRates, 0) - Math.max(...a.sampleRates, 0) || Math.max(...b.bitDepths, 0) - Math.max(...a.bitDepths, 0);
    if (sort === 'dynamic') return a.loudnessRangeLu === null ? (b.loudnessRangeLu === null ? 0 : 1) : b.loudnessRangeLu === null ? -1 : b.loudnessRangeLu - a.loudnessRangeLu;
    return text(a, b, 'artist') || (a.year ?? Infinity) - (b.year ?? Infinity) || text(a, b, 'title');
  });
  return result;
}
